'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { m as motion, LazyMotion, domAnimation } from 'motion/react';
import Image from 'next/image';
import { ResponsiveHeader } from './ResponsiveHeader';
import { ResponsiveFooter } from './ResponsiveFooter';
import { Cpu, CheckCircle2, Bot, ChevronDown, Network, Target } from 'lucide-react';
import ProjectEstimator from './ProjectEstimator';

const faqs = [
  {
    question: 'What is an AI Agent and how is it different from a chatbot?',
    answer:
      "While chatbots only answer questions based on a script or trained knowledge, AI Agents are autonomous systems capable of executing multi-step tasks, using software tools, and making decisions to achieve an outcome. They don't just talk; they *do* work for you.",
  },
  {
    question: 'How can AI Agents automate my business workflows?',
    answer:
      'AI Agents can handle repetitive, rule-based, and even cognitive tasks such as sorting emails, qualifying leads, routing customer support tickets, scheduling appointments, performing research, and syncing data across your CRM—giving your team more time for high-value strategic work.',
  },
  {
    question: 'Why should local companies invest in AI Agents in Hawaii?',
    answer:
      'Investing in AI Agents in Hawaii allows businesses to compete globally and operate efficiently despite timezone differences. Our custom AI Agents work 24/7, automating customer service, lead qualification, and internal workflows, helping Hawaii businesses scale rapidly without drastically increasing overhead costs.',
  },
  {
    question: 'Can AI Agents integrate with my current tools?',
    answer:
      'Yes! Whether you use Salesforce, HubSpot, Slack, Google Workspace, Shopify, or custom internal software, we can build AI Agents that connect smoothly to your existing tech stack via APIs and modern webhook integrations.',
  },
  {
    question: 'Where can I find expert developers for AI Agents in Hawaii?',
    answer:
      'Zumify is your premier local partner for building and integrating custom AI Agents in Hawaii. We understand the distinct operational challenges of island businesses and develop tailored AI automation solutions that connect seamlessly with your existing tools, providing you with a reliable digital workforce.',
  },
];

function FAQItem({
  question,
  answer,
  isOpen,
  onClick,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) {
  return (
    <div className="border-b border-white/10">
      <button
        className="w-full py-6 flex items-center justify-between text-left hover:text-purple-400 transition-colors group"
        onClick={onClick}
      >
        <span className="text-xl sm:text-2xl font-semibold text-white group-hover:text-purple-300 transition-colors">
          {question}
        </span>
        <ChevronDown
          className={`w-6 h-6 text-purple-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="overflow-hidden"
      >
        <p className="pb-6 text-stone-400 text-lg leading-relaxed">{answer}</p>
      </motion.div>
    </div>
  );
}

export function ResponsiveAIAgents() {
  const router = useRouter();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [isEstimatorOpen, setIsEstimatorOpen] = useState(false);

  // Reusing established homepage animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0 },
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0 },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const pulseSlow = {
    animate: {
      opacity: [0.3, 0.5, 0.3],
      scale: [1, 1.1, 1],
      transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' as const },
    },
  };

  return (
    <LazyMotion features={domAnimation}>
      <div className="min-h-screen bg-zinc-900 flex flex-col relative overflow-hidden">
        <ResponsiveHeader />

        {/* Hero / Information Section */}
        <section className="relative min-h-[60vh] flex items-center pt-32 lg:pt-56 pb-20">
          {/* Background Image Elements */}
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-zinc-900/80 sm:bg-zinc-900/70 z-10" />
            {/* Gradient mask to fade bottom edge into the next section */}
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-zinc-900 to-transparent z-10" />
            <div className="absolute inset-0 bg-[url('/images/development.webp')] bg-cover bg-center opacity-30 mix-blend-overlay" />
          </div>

          {/* Animated Glow Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            <motion.div
              className="absolute top-20 left-10 w-72 h-72 bg-purple-600/30 rounded-full blur-[100px]"
              variants={pulseSlow}
              animate="animate"
            />
            <motion.div
              className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-600/30 rounded-full blur-[100px]"
              animate={{
                opacity: [0.2, 0.4, 0.2],
                scale: [1, 1.2, 1],
                transition: { duration: 6, repeat: Infinity, ease: 'easeInOut' },
              }}
            />
          </div>

          <div className="relative w-full px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center z-10">
            <div className="max-w-4xl mx-auto backdrop-blur-sm sm:backdrop-blur-none bg-zinc-900/40 sm:bg-transparent p-6 rounded-3xl">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="space-y-8"
              >
                <motion.h1
                  variants={fadeInUp}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-[1.1] drop-shadow-2xl"
                >
                  AI Agent Integration <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 drop-shadow-md">
                    In Hawaii
                  </span>
                </motion.h1>

                <motion.p
                  variants={fadeInUp}
                  className="text-lg sm:text-lg md:text-xl text-stone-200 lg:text-stone-300 max-w-2xl mx-auto leading-relaxed drop-shadow-xl font-medium"
                >
                  Ready to scale your business with intelligent automation? Zumify designs and
                  engineers custom AI Agents that execute complex workflows, assist teams, and
                  interact with customers. We help modern businesses across Oahu, Maui, Kauai, and
                  the Big Island reduce costs and boost productivity by replacing manual work with
                  autonomous digital agents.
                </motion.p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Product Information / Impact Section */}
        <section className="px-4 sm:px-6 lg:px-8 pb-20 pt-10 lg:pt-20 relative">
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-16 lg:mb-20"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={fadeInUp}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Our AI Capabilities
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto rounded-full" />
              <p className="mt-6 text-lg text-stone-300 max-w-2xl mx-auto">
                Zumify leverages state-of-the-art Large Language Models (LLMs) and custom code to
                build digital workforce solutions tailored to your specific operational needs.
              </p>
            </motion.div>

            {/* Solutions & Capabilities Section - Layout */}
            <section className="px-4 sm:px-6 lg:px-8 py-20 lg:py-32 relative overflow-hidden">
              <div className="max-w-7xl mx-auto relative z-10">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
                  {/* Text Side */}
                  <motion.div
                    className="w-full lg:w-1/2 space-y-8"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={fadeInLeft}
                  >
                    <div className="space-y-4">
                      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                        Why Choose Zumify
                        <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">
                          For AI Integration
                        </span>
                      </h2>
                      <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full" />
                    </div>

                    <p className="text-lg text-stone-300 leading-relaxed">
                      AI isn't just a buzzword; it's a profound shift in how business gets done. We
                      don't just prompt basic chatbots—we architect capable systems wrapped in
                      modern security that seamlessly talk to your databases and APIs. We build AI
                      that brings tangible ROI to local Hawaii businesses.
                    </p>

                    <ul className="space-y-4">
                      {[
                        'Retrieval-Augmented Generation (RAG)',
                        'Outreach Automation',
                        'Accessing Business Data',
                        'Robust Guardrails for Safety and Quality',
                      ].map((item, idx) => (
                        <motion.li
                          key={idx}
                          className="flex items-center gap-3 text-stone-200"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 + idx * 0.1 }}
                        >
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center border border-cyan-500/30">
                            <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                          </div>
                          <span className="text-lg">{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>

                  {/* Picture Side */}
                  <motion.div
                    className="w-full lg:w-1/2 flex justify-center lg:justify-end"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={fadeInRight}
                  >
                    <div className="relative w-full max-w-[500px] aspect-[4/3] rounded-3xl bg-zinc-900 border border-white/10 shadow-2xl shadow-purple-500/20 overflow-hidden group">
                      {/* Decorative background glow */}
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-cyan-500/10 pointer-events-none" />

                      <div className="absolute inset-0 p-2 sm:p-3 lg:p-4 flex items-center justify-center pointer-events-none">
                        <div className="w-full h-full rounded-2xl md:rounded-[20px] bg-zinc-800/50 backdrop-blur-xl border border-white/10 overflow-hidden shadow-2xl relative group-hover:scale-[1.02] transition-transform duration-700 pointer-events-auto flex items-center justify-center">
                          <Bot className="w-32 h-32 text-cyan-400 opacity-80 drop-shadow-[0_0_30px_rgba(34,211,238,0.5)]" />
                          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/40 via-transparent to-transparent pointer-events-none rounded-xl md:rounded-[18px]" />
                        </div>
                      </div>

                      {/* Top-right corner accent */}
                      <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 blur-3xl pointer-events-none" />
                      {/* Bottom-left corner accent */}
                      <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/10 blur-3xl pointer-events-none" />
                    </div>
                  </motion.div>
                </div>
              </div>
            </section>

            {/* 4-Step Process Section - Scrolling Banners */}
            <section className="px-4 sm:px-6 lg:px-8 py-20 lg:py-32 bg-zinc-800/30 overflow-hidden relative border-y border-white/5">
              <div className="max-w-7xl mx-auto relative z-10">
                <motion.div
                  className="text-center mb-16 lg:mb-24"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                    Our AI Agent{' '}
                    <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                      Process
                    </span>
                  </h2>
                  <div className="h-1 w-24 bg-gradient-to-r from-cyan-500 to-purple-500 mx-auto rounded-full" />
                </motion.div>

                <div className="space-y-24 lg:space-y-40">
                  {[
                    {
                      step: '01',
                      icon: <Target className="w-10 h-10 text-blue-400" />,
                      title: 'Consultation & Discovery',
                      description:
                        'We start by consulting with our clients to deeply understand their workflows. During this phase, we pinpoint exactly what services and automations are needed to reduce manual work and drive business growth.',
                      color: 'blue',
                      bgColor: 'from-blue-500 to-transparent',
                      borderColor: 'border-blue-500/30',
                      shadow: 'shadow-[0_0_30px_rgba(59,130,246,0.3)]',
                      image: '/images/intro-meeting.webp',
                    },
                    {
                      step: '02',
                      icon: <Network className="w-10 h-10 text-cyan-400" />,
                      title: 'Agent Creation with n8n',
                      description:
                        'Next, we create the actual AI Agent using n8n, a powerful workflow automation tool. By integrating advanced LLMs, we construct a highly capable agent tailored to perform your specific tasks accurately and autonomously.',
                      color: 'cyan',
                      bgColor: 'from-cyan-500 to-transparent',
                      borderColor: 'border-cyan-500/30',
                      shadow: 'shadow-[0_0_30px_rgba(34,211,238,0.3)]',
                      image: '/images/development.webp',
                    },
                    {
                      step: '03',
                      icon: <Cpu className="w-10 h-10 text-purple-400" />,
                      title: 'App & Webhook Integration',
                      description:
                        'Finally, we create an intuitive app interface for your users. We connect this app directly to your AI Agents using secure webhook triggers, allowing for seamless, real-time interactions and autonomous task execution.',
                      color: 'purple',
                      bgColor: 'from-purple-500 to-transparent',
                      borderColor: 'border-purple-500/30',
                      shadow: 'shadow-[0_0_30px_rgba(168,85,247,0.3)]',
                      image: '/images/webhook-integration.webp',
                    },
                  ].map((step, idx) => {
                    const isEven = idx % 2 === 1;

                    return (
                      <div
                        key={idx}
                        className="relative z-10 flex flex-col lg:flex-row items-center gap-12 lg:gap-20"
                      >
                        {/* Text Side */}
                        <motion.div
                          className={`w-full lg:w-1/2 space-y-6 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true, amount: 0.3 }}
                          variants={isEven ? fadeInRight : fadeInLeft}
                        >
                          <div className="flex items-center gap-6 mb-2">
                            <span
                              className={`text-6xl lg:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-br ${step.bgColor} opacity-50`}
                            >
                              {step.step}
                            </span>
                            <div
                              className={`p-4 rounded-2xl bg-zinc-800 border ${step.borderColor} shadow-lg`}
                            >
                              {step.icon}
                            </div>
                          </div>

                          <h3 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
                            {step.title}
                          </h3>

                          <p className="text-lg text-stone-300 leading-relaxed max-w-xl">
                            {step.description}
                          </p>
                        </motion.div>

                        {/* Image / Visual Side */}
                        <motion.div
                          className={`w-full lg:w-1/2 ${isEven ? 'lg:order-1' : 'lg:order-2'}`}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true, amount: 0.3 }}
                          variants={isEven ? fadeInLeft : fadeInRight}
                        >
                          <div
                            className={`relative w-full aspect-[4/3] rounded-[24px] bg-zinc-900 border-2 ${step.borderColor} ${step.shadow} overflow-hidden group`}
                          >
                            {'image' in step && step.image ? (
                              <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900 overflow-hidden flex items-center justify-center p-6">
                                <img
                                  src={step.image}
                                  alt={step.title}
                                  className="w-full h-full object-contain rounded-xl group-hover:scale-105 transition-transform duration-700 relative z-10 shadow-xl shadow-black/50"
                                />
                              </div>
                            ) : (
                              <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center p-8">
                                <div
                                  className={`w-full h-full border border-white/5 rounded-2xl bg-gradient-to-br ${step.bgColor} backdrop-blur-3xl flex items-center justify-center group-hover:scale-105 transition-transform duration-700`}
                                >
                                  <div className="w-32 h-32 rounded-full bg-white/5 backdrop-blur-md flex items-center justify-center border border-white/10">
                                    {step.icon}
                                  </div>
                                </div>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                          </div>
                        </motion.div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            <div className="max-w-4xl mx-auto space-y-12 lg:space-y-16 mt-16">
              {/* Text Block 1 */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                className="bg-zinc-900/40 p-8 lg:p-10 rounded-3xl border border-white/5 backdrop-blur-sm relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0 w-1 p-0 h-full bg-gradient-to-b from-cyan-400 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 pl-4">
                  Retrieval-Augmented Generation (RAG)
                </h3>
                <p className="text-lg text-stone-300 leading-relaxed pl-4">
                  We use Retrieval-Augmented Generation (RAG) to safely train AI Agents on your
                  proprietary documents, onboarding manuals, technical specifications, and
                  historical data. Your AI Agent becomes a subject-matter expert on your business,
                  instantly providing accurate answers to your team or customers based exclusively
                  on your truth-source.
                </p>
              </motion.div>

              {/* Text Block 2 */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInLeft}
                className="bg-zinc-900/40 p-8 lg:p-10 rounded-3xl border border-white/5 backdrop-blur-sm relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0 w-1 pt-8 h-full bg-gradient-to-b from-purple-400 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 pl-4">
                  Outreach Automation
                </h3>
                <p className="text-lg text-stone-300 leading-relaxed pl-4">
                  Skip the manual cold emails and follow-ups. We build AI Agents that can
                  autonomously run your outreach workflows. From formatting and personalizing
                  messages to dispatching emails and syncing activity to your CRM, our agents handle
                  the initial contact so your team can focus entirely on closing deals and building
                  human relationships.
                </p>
              </motion.div>

              {/* Text Block 3 */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInRight}
                className="bg-zinc-900/40 p-8 lg:p-10 rounded-3xl border border-white/5 backdrop-blur-sm relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0 w-1 pt-8 h-full bg-gradient-to-b from-pink-400 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 pl-4">
                  Accessing Business Data
                </h3>
                <p className="text-lg text-stone-300 leading-relaxed pl-4">
                  Turn your natural language into complex database queries. We empower AI Agents to
                  securely connect to your ERPs, internal systems, and external APIs. This allows
                  your team and your applications to instantly access, summarize, and utilize
                  real-time business data exactly when it's needed, breaking down information silos
                  completely.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-24 bg-zinc-900/50 relative z-20">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Answers to Your Questions About AI
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-cyan-500 mx-auto rounded-full" />
            </motion.div>

            <div className="space-y-2">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* AI Agents Pricing Tiers Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-20 bg-zinc-900/50 relative z-20">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                AI Agents Pricing{' '}
                <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                  Tiers
                </span>
              </h2>
              <p className="text-lg sm:text-xl text-stone-300 max-w-3xl mx-auto">
                Explore our Price Tiers of the products we craft for Hawaii's businesses.
                <br className="hidden sm:block" />
                <span className="text-stone-400 text-sm sm:text-base mt-2 block">
                  Note: All quotes will be finalized based on complexity, time, and landscape of
                  project proposal. Minimums enforced.
                </span>
              </p>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-8 flex justify-center"
              >
                <button
                  onClick={() => setIsEstimatorOpen(true)}
                  className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-bold py-3 px-8 rounded-full shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] transition-all transform hover:scale-105 flex items-center gap-2"
                >
                  Try Our Project Estimator
                </button>
              </motion.div>
            </motion.div>

            <div className="space-y-12">
              {/* Design Work / AI Agents Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="border border-purple-500/30 rounded-2xl p-4 sm:p-6 bg-zinc-900/50"
              >
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Left Panel */}
                  <div className="lg:w-1/4 bg-black rounded-xl p-6 flex flex-col justify-end min-h-[300px] relative overflow-hidden border border-white/5 group">
                    {/* Poster Background */}
                    <Image
                      src="/images/workingWebDesign.webp"
                      alt="Design & AI Agents Tier Poster"
                      fill
                      className="object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-500"
                    />
                    {/* Decorative Background for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
                    <div className="absolute inset-0 opacity-40 bg-gradient-to-b from-purple-500/20 to-transparent pointer-events-none mix-blend-overlay" />

                    <div className="relative z-10 mt-auto">
                      <h3 className="text-2xl font-bold text-white mb-2">
                        Automated Agents
                      </h3>
                      <div className="space-y-1">
                        <p className="text-sm text-stone-400">Starter Agents</p>
                        <p className="text-sm text-stone-400">Advanced Agents</p>
                        <p className="text-sm text-stone-400">Enterprise Agents</p>
                      </div>
                    </div>
                  </div>

                  <div className="lg:w-3/4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Tier 1 */}
                    <div className="bg-zinc-800/50 rounded-xl p-6 border border-cyan-500/20 flex flex-col">
                      <h4 className="text-xl font-semibold text-cyan-400 mb-4 text-center">
                        Starter Agents
                      </h4>
                      <ul className="space-y-3 mb-8 flex-1">
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>
                            Pre-built AI agent templates (chat, email response, reminders, simple tasks)
                          </span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>Integration with 1-2 platforms (e.g., Slack, Gmail, or web apps)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>Rule-based workflows with limited customization</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>Basic analytics and performance tracking</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>
                            Ideal for small businesses or teams starting to explore AI automation
                          </span>
                        </li>
                      </ul>
                      <div className="text-center pt-4 border-t border-white/10 space-y-4">
                        <div>
                          <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                          <p className="text-xl font-bold text-cyan-400">$100 / hr</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">10-20</span></p>
                            <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-cyan-400">$1,000-$2,000</span></p>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-white/10">
                          <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                          <p className="text-lg font-bold text-white">Starter Plan</p>
                          <p className="text-cyan-400">$99.99 / month</p>
                        </div>
                      </div>
                    </div>

                    {/* Tier 2 */}
                    <div className="bg-zinc-800/50 rounded-xl p-6 border border-purple-500/20 flex flex-col">
                      <h4 className="text-xl font-semibold text-purple-400 mb-4 text-center">
                        Advanced Agents
                      </h4>
                      <ul className="space-y-3 mb-8 flex-1">
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>Fully customized AI agent logic for your business processes</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>
                            Integration with multiple platforms (CRM, marketing tools, internal apps)
                          </span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>Advanced workflow automation, triggers, multi-step processes</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>Analytics, reporting, and performance optimization</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>
                            Scalable for teams, SaaS platforms, or enterprise-level operations
                          </span>
                        </li>
                      </ul>
                      <div className="text-center pt-4 border-t border-white/10 space-y-4">
                        <div>
                          <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                          <p className="text-xl font-bold text-purple-400">$125 / hr</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">25-40</span></p>
                            <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-purple-400">$3,125-$5,000</span></p>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-white/10">
                          <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                          <p className="text-lg font-bold text-white">Growth Plan</p>
                          <p className="text-purple-400">$199.99 / month</p>
                        </div>
                      </div>
                    </div>

                    {/* Tier 3 */}
                    <div className="bg-zinc-800/50 rounded-xl p-6 border border-pink-500/20 flex flex-col">
                      <h4 className="text-xl font-semibold text-pink-400 mb-4 text-center">
                        Enterprise Agents
                      </h4>
                      <ul className="space-y-3 mb-8 flex-1">
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>Large-scale multi-agent systems and custom LLM deployment</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>
                            Complete enterprise software integration (ERP, custom databases, legacy systems)
                          </span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>Advanced security, compliance features, and self-hosted options</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>Dedicated support and continuous workflow optimization</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>
                            Built for large corporations requiring massive scale and complete control
                          </span>
                        </li>
                      </ul>
                      <div className="text-center pt-4 border-t border-white/10 space-y-4">
                        <div>
                          <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                          <p className="text-xl font-bold text-pink-400">$150 / hr</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">70+</span></p>
                            <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-pink-400">$10,500+</span></p>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-white/10">
                          <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                          <p className="text-lg font-bold text-white">Premium Plan</p>
                          <p className="text-pink-400">$999.99 / month</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Project Estimator Modal */}
          <ProjectEstimator isOpen={isEstimatorOpen} onClose={() => setIsEstimatorOpen(false)} />
        </section>

        {/* CTA Bottom Section */}
        <motion.section
          className="px-4 sm:px-6 lg:px-8 py-20 mb-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          variants={fadeInUp}
        >
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-pink-500/10 backdrop-blur-xl border border-white/10 rounded-3xl p-8 lg:p-12 shadow-[0_0_50px_rgba(168,85,247,0.15)]">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to automate with an AI Agent?
            </h2>
            <p className="text-lg sm:text-xl text-stone-300 mb-8 max-w-2xl mx-auto">
              Schedule a free consultation call to explore how intelligent automation can transform
              your workflows.
            </p>
            <button
              onClick={() => router.push('/contact')}
              className="px-10 py-4 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white rounded-full font-bold text-lg hover:scale-105 transition-transform duration-300 shadow-lg shadow-purple-500/30"
            >
              Get Started Today
            </button>
          </div>
        </motion.section>

        <ResponsiveFooter />
      </div>
    </LazyMotion>
  );
}
